import type { ReactNode } from 'react';
import { Box, Button, Flex, Heading, Stack, Text, Tooltip } from '@customerio/pluma-components/react';
import { formatCentsToUsd } from '@/utils/currency';
import { formatNumber } from '@/utils/number';
import type { CreditsPurchaseTermsLinks } from '../utils/credits-purchase-config';
import { CreditsPurchaseDisclaimer } from './credits-purchase-disclaimer';

export type OrdwayPurchaseFormBodyProps = {
  /** Heading above the quantity control, e.g. `Credits` or `Previews`. */
  sectionHeading: string;
  /** The quantity control: a pack-count input for AI credits, pack radios for Inbox Previews. */
  children: ReactNode;
  totalPriceCents: number;
  totalCredits: number;
  /** Plural noun for the credits total, e.g. `credits` or `previews`. */
  unitLabel: string;
  /** The full fine print, including how the purchase is billed; each form states its own. */
  disclaimer: string;
  /** Which terms the fine print cites; defaults to Feature Terms and Terms of Service. */
  termsLinks?: CreditsPurchaseTermsLinks;
  submitLabel: string;
  isSubmitDisabled: boolean;
  /** Why submit is disabled, shown as a tooltip. `''` when it is not. */
  submitDisabledMessage: string;
  isSubmitting: boolean;
  onCancel: () => void;
};

/**
 * Everything both Ordway purchase forms render around their quantity control: the section heading,
 * the total, the disclaimer, and the actions. The forms differ only in that control and in which
 * endpoint they submit to, so this holds the rest once.
 *
 * There is no tax line and no price breakdown — Ordway posts the purchase as an invoice and then
 * collects it from the card on file, so the total is simply what was ordered. Cancel stays enabled
 * whenever a purchase is not in flight, including after a failure that locked submit: reopening the
 * form is how a customer buys again.
 */
export function OrdwayPurchaseFormBody({
  sectionHeading,
  children,
  totalPriceCents,
  totalCredits,
  unitLabel,
  disclaimer,
  termsLinks,
  submitLabel,
  isSubmitDisabled,
  submitDisabledMessage,
  isSubmitting,
  onCancel,
}: OrdwayPurchaseFormBodyProps) {
  return (
    <>
      <Stack gap="150">
        <Heading level="2" size="sm">
          {sectionHeading}
        </Heading>

        {children}

        <Box data-test-credits-price-total>
          <Text weight="bold">Total: {formatCentsToUsd(totalPriceCents)}</Text>
          <Text color="secondary">
            {' '}
            ({formatNumber(totalCredits)} {unitLabel})
          </Text>
        </Box>
      </Stack>
      <CreditsPurchaseDisclaimer disclaimer={disclaimer} termsLinks={termsLinks} />
      <Flex mt="300" justifyContent="flex-end" gap="200">
        <Button data-test-credits-purchase-cancel variant="secondary" isDisabled={isSubmitting} onClick={onCancel}>
          Cancel
        </Button>

        <Tooltip
          content={submitDisabledMessage}
          isDisabled={submitDisabledMessage === ''}
          isCustom={true}
          placement="top"
        >
          {(context) => (
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              data-test-credits-purchase-submit
              variant="primary"
              type="submit"
              isDisabled={isSubmitDisabled}
              isLoading={isSubmitting}
            >
              {submitLabel}
            </Button>
          )}
        </Tooltip>
      </Flex>
    </>
  );
}
